import { useState, useRef, useEffect, useCallback, memo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { Send, Loader2, MessageCircle, X, ArrowLeft, Plus, Users } from 'lucide-react';
import { useStaffChat, ChatConversation, ChatMessage } from '@/hooks/useStaffChat';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

const StaffChatButton = memo(({
  isOpen,
  onClick,
  unreadCount,
}: {
  isOpen: boolean;
  onClick: () => void;
  unreadCount: number;
}) => (
  <Button
    onClick={onClick}
    size="lg"
    className={cn(
      'fixed bottom-6 right-24 z-50 w-14 h-14 rounded-full shadow-lg',
      'transition-all duration-300 ease-in-out',
      'hover:scale-110 hover:shadow-xl',
      isOpen
        ? 'bg-muted-foreground hover:bg-muted-foreground/90'
        : 'bg-gradient-to-br from-green-600 to-green-500 hover:from-green-500 hover:to-green-400'
    )}
  >
    {isOpen ? (
      <X className="w-6 h-6" />
    ) : (
      <>
        <MessageCircle className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center text-white font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </>
    )}
  </Button>
));

StaffChatButton.displayName = 'StaffChatButton';

const ConversationItem = memo(({
  conversation,
  currentUserId,
  isActive,
  onClick,
}: {
  conversation: ChatConversation;
  currentUserId?: string;
  isActive: boolean;
  onClick: () => void;
}) => {
  const otherParticipants = conversation.participants?.filter(
    p => p.user_id !== currentUserId
  ) || [];
  const displayName = conversation.type === "group"
    ? conversation.name || 'Grupo'
    : otherParticipants[0]?.user?.full_name || 'Usuario';

  const initials = displayName
    .split(' ')
    .map(n => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left',
        isActive ? 'bg-primary/10' : 'hover:bg-muted'
      )}
    >
      <Avatar className="h-10 w-10">
        <AvatarFallback className={conversation.type === "group" ? 'bg-blue-500' : 'bg-green-500'}>
          {conversation.type === "group" ? <Users className="w-4 h-4" /> : initials}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{displayName}</p>
        {conversation.last_message && (
          <p className="text-xs text-muted-foreground truncate">
            {conversation.last_message.content}
          </p>
        )}
      </div>
      {(conversation.unread_count ?? 0) > 0 && (
        <span className="w-5 h-5 bg-primary rounded-full text-xs flex items-center justify-center text-primary-foreground">
          {conversation.unread_count}
        </span>
      )}
    </button>
  );
});

ConversationItem.displayName = 'ConversationItem';

const MessageBubble = memo(({
  message,
  isOwn,
}: {
  message: ChatMessage;
  isOwn: boolean;
}) => (
  <div className={cn('flex gap-2', isOwn ? 'flex-row-reverse' : '')}>
    {!isOwn && (
      <Avatar className="h-8 w-8 flex-shrink-0">
        <AvatarFallback className="bg-green-500 text-xs">
          {message.sender?.full_name?.slice(0, 2).toUpperCase() || '??'}
        </AvatarFallback>
      </Avatar>
    )}
    <div
      className={cn(
        'max-w-[75%] rounded-lg px-3 py-2',
        isOwn
          ? 'bg-primary text-primary-foreground'
          : 'bg-muted'
      )}
    >
      {!isOwn && (
        <p className="text-xs font-medium mb-1">{message.sender?.full_name}</p>
      )}
      <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
      <p className={cn(
        'text-[10px] mt-1',
        isOwn ? 'text-primary-foreground/70' : 'text-muted-foreground'
      )}>
        {formatDistanceToNow(new Date(message.created_at), { addSuffix: true, locale: es })}
      </p>
    </div>
  </div>
));

MessageBubble.displayName = 'MessageBubble';

export const StaffChat = memo(() => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    conversations,
    messages,
    availableUsers,
    activeConversationId,
    setActiveConversationId,
    sendMessage,
    startConversation,
    loadingMessages,
    isSending,
    totalUnread,
    currentUserId,
    canSendMessages,
  } = useStaffChat();

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input when conversation opens
  useEffect(() => {
    if (activeConversationId && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [activeConversationId]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inputValue.trim();
    if (!trimmed || isSending) return;

    setInputValue('');
    await sendMessage(trimmed);
  }, [inputValue, isSending, sendMessage]);

  const handleStartConversation = useCallback(async (userId: string) => {
    await startConversation(userId);
    setShowNewChat(false);
  }, [startConversation]);

  const handleBack = useCallback(() => {
    if (showNewChat) {
      setShowNewChat(false);
    } else {
      setActiveConversationId(null);
    }
  }, [showNewChat, setActiveConversationId]);

  const activeConversation = conversations.find(c => c.id === activeConversationId);
  const otherParticipant = activeConversation?.participants?.find(
    p => p.user_id !== currentUserId
  );
  const conversationTitle = activeConversation?.type === 'group'
    ? activeConversation.name || 'Grupo'
    : otherParticipant?.user?.full_name || 'Chat';

  return (
    <>
      <StaffChatButton
        isOpen={isOpen}
        onClick={() => setIsOpen(prev => !prev)}
        unreadCount={totalUnread}
      />

      <div
        className={cn(
          'fixed bottom-24 right-24 z-50 w-[380px] max-w-[calc(100vw-3rem)]',
          'transition-all duration-300 ease-in-out transform',
          isOpen
            ? 'opacity-100 translate-y-0 scale-100'
            : 'opacity-0 translate-y-4 scale-95 pointer-events-none'
        )}
      >
        <Card className="flex flex-col h-[500px] max-h-[calc(100vh-10rem)] shadow-2xl border-2">
          {/* Header */}
          <div className="flex items-center gap-3 p-4 border-b bg-gradient-to-r from-green-500/10 to-transparent">
            {(activeConversationId || showNewChat) && (
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleBack}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            <div className="flex items-center gap-3 flex-1">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold">
                  {showNewChat ? 'Nueva conversacion' : (activeConversationId ? conversationTitle : 'Chat del equipo')}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {activeConversationId ? 'En linea' : `${conversations.length} conversaciones`}
                </p>
              </div>
            </div>
            {!activeConversationId && !showNewChat && canSendMessages && (
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowNewChat(true)}>
                <Plus className="w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Content */}
          {showNewChat ? (
            <ScrollArea className="flex-1 p-2">
              <p className="text-sm text-muted-foreground px-2 py-1">Selecciona un usuario:</p>
              {availableUsers.map(user => (
                <button
                  key={user.id}
                  onClick={() => handleStartConversation(user.id)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors text-left"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-green-500">
                      {user.full_name?.slice(0, 2).toUpperCase() || '??'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{user.full_name}</p>
                    {user.employee_id && (
                      <p className="text-xs text-muted-foreground">{user.employee_id}</p>
                    )}
                  </div>
                </button>
              ))}
            </ScrollArea>
          ) : activeConversationId ? (
            <>
              <ScrollArea className="flex-1 p-4" ref={scrollRef}>
                <div className="space-y-4">
                  {loadingMessages ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No hay mensajes aun</p>
                      <p className="text-xs">Envia el primero!</p>
                    </div>
                  ) : (
                    messages.map(msg => (
                      <MessageBubble
                        key={msg.id}
                        message={msg}
                        isOwn={msg.sender_id === currentUserId}
                      />
                    ))
                  )}
                </div>
              </ScrollArea>

              {canSendMessages ? (
                <form onSubmit={handleSubmit} className="p-4 border-t">
                  <div className="flex gap-2">
                    <Input
                      ref={inputRef}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder="Escribe un mensaje..."
                      disabled={isSending}
                      className="flex-1"
                    />
                    <Button
                      type="submit"
                      size="icon"
                      disabled={!inputValue.trim() || isSending}
                      className="bg-green-600 hover:bg-green-500"
                    >
                      {isSending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="p-4 border-t bg-muted/50 text-center text-sm text-muted-foreground">
                  Solo puedes recibir mensajes
                </div>
              )}
            </>
          ) : (
            <ScrollArea className="flex-1 p-2">
              {conversations.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No hay conversaciones</p>
                  <p className="text-xs">Inicia una nueva con el boton +</p>
                </div>
              ) : (
                conversations.map(conv => (
                  <ConversationItem
                    key={conv.id}
                    conversation={conv}
                    currentUserId={currentUserId}
                    isActive={conv.id === activeConversationId}
                    onClick={() => setActiveConversationId(conv.id)}
                  />
                ))
              )}
            </ScrollArea>
          )}
        </Card>
      </div>
    </>
  );
});

StaffChat.displayName = 'StaffChat';
